import { ComparisonBlock, ScoreValue } from "@foodpilot/foods";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import type { ScoreImprovement } from "src/types";
import { formatNumber } from "src/utils/formatting";

type ProductScoreEvolutionCardProps = {
    currentValue: NumericalScoreValue;
    improvement: ScoreImprovement;
    precision: number;
    previousCampaignLabel?: string;
    previousValue: NumericalScoreValue;
    unit: string;
};

const getComparisonSurface = (currentValue: number, previousValue: number, improvement: ScoreImprovement) => {
    if (currentValue === previousValue) return "neutral";

    const improves = improvement === "decrease" ? currentValue < previousValue : currentValue > previousValue;

    return improves ? "positive" : "negative";
};

/** Product V2 score and campaign evolution, aligned with the product edition score card. */
export const ProductScoreEvolutionCard = (props: ProductScoreEvolutionCardProps) => {
    const { currentValue, improvement, precision, previousCampaignLabel, previousValue, unit } = props;
    const { i18n, t } = useTranslation();
    const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const formattedCurrentValue =
        currentValue === null ? null : (
            formatNumber(currentValue, language, {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
            })
        );
    const comparisonSurface =
        currentValue !== null && previousValue !== null ?
            getComparisonSurface(currentValue, previousValue, improvement)
        :   null;

    return (
        <Box
            aria-label={t("product.score_details.evolution.summary", "Score and evolution")}
            data-testid="product-score-evolution-card"
            sx={(theme) => ({
                backgroundColor: theme.palette.primary[600],
                borderRadius: theme.spacing(1.5),
                boxShadow: theme.custom.shadows.grey,
                boxSizing: "border-box",
                color: theme.custom.grey[100],
                overflow: "hidden",
                padding: theme.spacing(2),
                width: "100%",
            })}
        >
            <Stack gap={2}>
                {formattedCurrentValue === null ?
                    <ScoreValue
                        emptyLabel={t("product.score_details.score_unavailable", "Score unavailable")}
                        unit={unit}
                        value={null}
                    />
                :   <ScoreValue unit={unit} value={formattedCurrentValue} />}

                {currentValue !== null && previousValue !== null && comparisonSurface !== null && (
                    <Box
                        sx={(theme) => ({
                            backgroundColor:
                                comparisonSurface === "positive" ? theme.custom.status.green.surface
                                : comparisonSurface === "negative" ? theme.custom.red[50]
                                : theme.custom.grey[300],
                            borderRadius: theme.spacing(1.5),
                            boxSizing: "border-box",
                            color: theme.custom.grey[2800],
                            minWidth: 0,
                            padding: theme.spacing(1),
                            width: "100%",
                            "& > div > .comparison-block-wrapper": {
                                alignItems: "center",
                                columnGap: theme.spacing(0.25),
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                rowGap: theme.spacing(0.5),
                                width: "100%",
                            },
                            "& > div > .comparison-block-wrapper > .comparison-block-wrapper": {
                                flexShrink: 0,
                            },
                            "& > div > .comparison-block-wrapper > .comparison-block-wrapper > .MuiBox-root": {
                                marginRight: theme.spacing(0.25),
                            },
                            "& > div > .comparison-block-wrapper > :last-child": {
                                display: "contents",
                            },
                            "& > div > .comparison-block-wrapper > :last-child > .MuiTypography-root": {
                                maxWidth: "100%",
                                whiteSpace: "normal",
                            },
                            "& > div > .comparison-block-wrapper > :last-child > .MuiStack-root": {
                                flexBasis: "100%",
                                whiteSpace: "nowrap",
                            },
                        })}
                    >
                        <ComparisonBlock
                            comparedTo={{
                                label:
                                    previousCampaignLabel ??
                                    t("product.score_details.evolution.previous_campaign", "Previous campaign"),
                                unit,
                                value: previousValue,
                            }}
                            comparisonText={t("score_reference.in_relation", "in relation to")}
                            currentValue={currentValue}
                            improvement={improvement}
                            layout={{ direction: "horizontal-2-rows", size: "M" }}
                            locale={language}
                        />
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
